// react
import { useCallback, useEffect, useState } from 'react';

// services
import {
	fetchPaymentsOverview,
	PaymentsOverview,
} from '../../../../services/api';

// Reads the project-wide per-bucket voucher counts + per-currency totals from
// the `/v2/data/payments-overview` endpoint. One round trip, then re-fetched
// whenever `refreshToken` changes (callers bump it after a voucher mutation).
//
// Returns `null` for `overview` while loading or if the request failed; the
// Payments page degrades to "—" in tiles/badges rather than blocking render.

export interface UsePaymentsOverviewResult {
	overview: PaymentsOverview | null;
	loading: boolean;
	error: string | null;
	refresh: () => void;
}

export const usePaymentsOverview = (
	projectId: number | undefined,
	refreshToken = 0
): UsePaymentsOverviewResult => {
	const [overview, setOverview] = useState<PaymentsOverview | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [manualToken, setManualToken] = useState(0);

	const refresh = useCallback(() => setManualToken((t) => t + 1), []);

	useEffect(() => {
		if (!projectId) {
			setOverview(null);
			return;
		}

		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		if (!accessToken || !idToken) {
			setError('Not authenticated');
			return;
		}

		let cancelled = false;
		setLoading(true);
		setError(null);

		fetchPaymentsOverview({ accessToken, idToken }, projectId)
			.then((res) => {
				if (cancelled) return;
				if (res.error) {
					setError(res.error.message || 'Failed to load payments overview');
					setOverview(null);
				} else {
					setOverview(res.data ?? null);
				}
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof Error ? err.message : 'Failed to load payments overview');
				setOverview(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [projectId, refreshToken, manualToken]);

	return { overview, loading, error, refresh };
};
